import React, { useState, useEffect } from "react";
import "./ScholarshipBanner.css";

// Import both mobile and web images
import scholarshipBannerMobile from "../../../assets/svg/ScholarshipBanner/scholarship-banner-mobile.svg";
import scholarshipBannerWeb from "../../../assets/svg/ScholarshipBanner/scholarship-banner-web.svg";
import CONFIG_VARS from "../../../constants/configuration";

const SCHOLARSHIP_RULES_URL = `${CONFIG_VARS.pip_GCS_url}/SE_SCHOLARSHIP/scholarship_rules.json`;

const ScholarshipBanner = ({ grade, examType, totalMarks, marksObtained }) => {
  const [scholarshipAmount, setScholarshipAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rulesData, setRulesData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 700;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const fetchScholarshipRules = async () => {
      try {
        setLoading(true);
        const response = await fetch(SCHOLARSHIP_RULES_URL);

        if (!response.ok) {
          throw new Error(`Failed to fetch rules: ${response.status}`);
        }

        const data = await response.json();
        setRulesData(data);
      } catch (err) {
        console.error("Error loading scholarship rules:", err);
        setError("Failed to load scholarship rules");
      } finally {
        setLoading(false);
      }
    };

    fetchScholarshipRules();
  }, []);

  useEffect(() => {
    if (!rulesData) return;

    const calculateScholarship = () => {
      if (!grade || !examType || !totalMarks || marksObtained === undefined || marksObtained === null) {
        setScholarshipAmount(null);
        return;
      }

      try {
        const safeMarksObtained = Math.max(0, parseFloat(marksObtained));
        const safeTotalMarks = Math.max(1, parseFloat(totalMarks));

        const percentage = (safeMarksObtained / safeTotalMarks) * 100;

        const amount = findMatchingScholarship(grade, examType, percentage);
        setScholarshipAmount(amount);
      } catch (err) {
        console.error("Error calculating scholarship:", err);
        setScholarshipAmount(null);
      }
    };

    const normalizeExamType = (examType) => {
      if (!examType) return "";

      const lowerExam = examType.toLowerCase().trim();

      if (lowerExam.includes("jee") || lowerExam.includes("iit")) {
        return "iit_jee";
      }

      if (lowerExam.includes("neet")) {
        return "neet";
      }

      if (lowerExam.includes("foundation") || lowerExam.includes("foundational")) {
        return "foundation";
      }

      return lowerExam.replace(/\s+/g, "_");
    };

    /**
     * Dynamically checks if a percentage falls within a range string
     * Supports formats: "0-50", "51-60", "61-100", "0-20", "21-30", etc.
     */
    const isPercentageInRange = (percentage, rangeStr) => {
      // Handle edge cases
      if (!rangeStr || typeof rangeStr !== "string") return false;

      // Remove any whitespace
      rangeStr = rangeStr.trim();

      // Split by hyphen
      const parts = rangeStr.split("-");
      if (parts.length !== 2) return false;

      let min = parseFloat(parts[0]);
      let max = parseFloat(parts[1]);

      if (isNaN(min) || isNaN(max)) return false;

      // Special handling for ranges that might be inclusive/exclusive
      // Check if it's the last range (likely includes 100)
      const isLastRange = max === 100;

      if (isLastRange) {
        // Last range includes everything up to and including 100
        return percentage >= min && percentage <= max;
      } else {
        // Middle ranges: min inclusive, max inclusive
        // For percentage ranges, we want inclusive on both ends typically
        return percentage >= min && percentage <= max;
      }
    };

    const findMatchingScholarship = (grade, examType, percentage) => {
      const gradeStr = grade.toString();

      // Find the grade object in rulesData
      const gradeObject = rulesData.find((item) => item[gradeStr]);

      if (!gradeObject) {
        console.log(`No grade object found for grade: ${gradeStr}`);
        return null;
      }

      const gradeRules = gradeObject[gradeStr];
      const normalizedExamType = normalizeExamType(examType);

      if (!normalizedExamType) {
        console.log(`Invalid exam type: ${examType}`);
        return null;
      }

      // Find the exam object
      const examObject = gradeRules.find((item) => item[normalizedExamType]);

      if (!examObject) {
        console.log(`No exam object found for: ${normalizedExamType} in grade ${gradeStr}`);
        return null;
      }

      const examRules = examObject[normalizedExamType];

      // Dynamically find the matching percentage range
      let matchingAmount = null;
      let matchedRange = null;

      // Sort ranges by their minimum value to ensure proper matching
      const sortedRanges = Object.keys(examRules).sort((a, b) => {
        const minA = parseFloat(a.split("-")[0]);
        const minB = parseFloat(b.split("-")[0]);
        return minA - minB;
      });

      for (const range of sortedRanges) {
        if (isPercentageInRange(percentage, range)) {
          matchingAmount = parseFloat(examRules[range]);
          matchedRange = range;
          break;
        }
      }

      if (matchingAmount !== null) {
        console.log(
          `Scholarship matched: ₹${matchingAmount.toLocaleString()} for ${percentage.toFixed(
            2
          )}% (range: ${matchedRange}) for Grade ${gradeStr}, Exam: ${normalizedExamType}`
        );
      } else {
        console.log(
          `No scholarship range matched for ${percentage.toFixed(2)}% in Grade ${gradeStr}, Exam: ${normalizedExamType}`
        );
        console.log(`Available ranges: ${sortedRanges.join(", ")}`);
      }

      return matchingAmount;
    };

    calculateScholarship();
  }, [grade, examType, totalMarks, marksObtained, rulesData]);

  const bannerImage = isMobile ? scholarshipBannerMobile : scholarshipBannerWeb;

  if (loading) {
    return <div className="scholarship-banner scholarship-loading">Loading scholarship information...</div>;
  }

  if (error) {
    return <div className="scholarship-banner scholarship-error">{error}</div>;
  }

  if (!scholarshipAmount || !rulesData) {
    return null;
  }

  return (
    <div className="banner-container">
      <img
        src={bannerImage}
        alt={`Scholarship of ₹${scholarshipAmount.toLocaleString()}`}
        className="banner-image"
        title={`You won a scholarship of ₹${scholarshipAmount.toLocaleString()}`}
      />
      <h1 className="scholarship-banner_amount">₹ {scholarshipAmount.toLocaleString()}</h1>
    </div>
  );
};

export default ScholarshipBanner;
